import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import {
  BarChart3,
  TrendingUp,
  Package,
  Euro,
  Calendar,
  ShoppingCart
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const ReportsPage = () => {
  const [sales, setSales] = useState([]);
  const [stock, setStock] = useState([]);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  useEffect(() => {
    fetchData();
  }, [days]);

  const fetchData = async () => {
    try {
      const [salesRes, stockRes] = await Promise.all([
        axios.get(`${API}/sales?days=${days}`),
        axios.get(`${API}/stock`)
      ]);
      setSales(salesRes.data);
      setStock(stockRes.data);
    } catch (err) {
      toast.error("Fehler beim Laden der Berichte");
    } finally {
      setLoading(false);
    }
  };

  // Calculate statistics
  const totalSales = sales.length;
  const totalRevenue = sales.reduce((sum, s) => sum + (s.sale_price || 0) * (s.quantity || 1), 0);
  
  // Sales by product
  const salesByProduct = sales.reduce((acc, s) => {
    const name = s.product_name || "Unbekannt";
    if (!acc[name]) acc[name] = { count: 0, revenue: 0 };
    acc[name].count += s.quantity || 1;
    acc[name].revenue += (s.sale_price || 0) * (s.quantity || 1);
    return acc;
  }, {});

  const topProducts = Object.entries(salesByProduct)
    .map(([name, data]) => ({ name, ...data }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 10);

  // Sales by day
  const salesByDay = sales.reduce((acc, s) => {
    const date = new Date(s.sold_at).toLocaleDateString("de-DE");
    if (!acc[date]) acc[date] = { count: 0, revenue: 0 };
    acc[date].count += s.quantity || 1;
    acc[date].revenue += (s.sale_price || 0) * (s.quantity || 1);
    return acc;
  }, {});

  // Stock status
  const stockSummary = {
    total: stock.length,
    ok: stock.filter(s => s.status === "ok").length,
    low: stock.filter(s => s.status === "low").length,
    empty: stock.filter(s => s.status === "empty").length,
    expiring: stock.filter(s => s.expiry_status === "critical" || s.expiry_status === "warning").length
  };

  const avgDailySales = Object.keys(salesByDay).length > 0 
    ? totalRevenue / Object.keys(salesByDay).length 
    : 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-rose-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="reports-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold text-slate-900 tracking-tight">
            Berichte
          </h1>
          <p className="text-slate-500 mt-1">
            Verkaufsstatistiken und Bestandsübersicht
          </p>
        </div>
        <Select value={String(days)} onValueChange={(v) => setDays(parseInt(v))}>
          <SelectTrigger className="w-40">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7">Letzte 7 Tage</SelectItem>
            <SelectItem value="30">Letzte 30 Tage</SelectItem>
            <SelectItem value="90">Letzte 90 Tage</SelectItem>
            <SelectItem value="365">Letztes Jahr</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-emerald-200 bg-gradient-to-br from-emerald-50 to-white">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-emerald-600 font-medium">Umsatz</p>
                <p className="text-3xl font-bold text-emerald-700 mt-1">
                  {totalRevenue.toFixed(2)}€
                </p>
                <p className="text-xs text-emerald-600 mt-1">
                  ⌀ {avgDailySales.toFixed(2)}€/Tag
                </p>
              </div>
              <Euro className="w-8 h-8 text-emerald-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-blue-200 bg-gradient-to-br from-blue-50 to-white">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-blue-600 font-medium">Verkäufe</p>
                <p className="text-3xl font-bold text-blue-700 mt-1">
                  {totalSales}
                </p>
                <p className="text-xs text-blue-600 mt-1">
                  in {Object.keys(salesByDay).length} Tagen
                </p>
              </div>
              <ShoppingCart className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-purple-200 bg-gradient-to-br from-purple-50 to-white">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-purple-600 font-medium">Produkte</p>
                <p className="text-3xl font-bold text-purple-700 mt-1">
                  {stockSummary.total}
                </p>
                <p className="text-xs text-purple-600 mt-1">
                  {stockSummary.low + stockSummary.empty} nachfüllen
                </p>
              </div>
              <Package className="w-8 h-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-amber-200 bg-gradient-to-br from-amber-50 to-white">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-amber-600 font-medium">MHD kritisch</p>
                <p className="text-3xl font-bold text-amber-700 mt-1">
                  {stockSummary.expiring}
                </p>
                <p className="text-xs text-amber-600 mt-1">
                  Produkte ablaufend
                </p>
              </div>
              <Calendar className="w-8 h-8 text-amber-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Products */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5" />
              Top Produkte
            </CardTitle>
          </CardHeader>
          <CardContent>
            {topProducts.length === 0 ? (
              <p className="text-center text-slate-500 py-8">
                Keine Verkäufe in diesem Zeitraum
              </p>
            ) : (
              <div className="space-y-3">
                {topProducts.map((product, idx) => (
                  <div key={product.name} className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                      idx === 0 ? 'bg-amber-100 text-amber-700' :
                      idx === 1 ? 'bg-slate-200 text-slate-600' :
                      idx === 2 ? 'bg-orange-100 text-orange-700' :
                      'bg-slate-100 text-slate-500'
                    }`}>
                      {idx + 1}
                    </div>
                    <div className="flex-1">
                      <p className="font-medium truncate">{product.name}</p>
                      <div className="h-2 bg-slate-100 rounded-full mt-1 overflow-hidden">
                        <div
                          className="h-full bg-rose-500 rounded-full"
                          style={{ 
                            width: `${(product.count / topProducts[0].count) * 100}%` 
                          }}
                        />
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold">{product.count}x</p>
                      <p className="text-sm text-slate-500">{product.revenue.toFixed(2)}€</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Daily Sales */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5" />
              Tagesumsätze
            </CardTitle>
          </CardHeader>
          <CardContent>
            {Object.keys(salesByDay).length === 0 ? (
              <p className="text-center text-slate-500 py-8">
                Keine Verkäufe in diesem Zeitraum
              </p>
            ) : (
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {Object.entries(salesByDay)
                  .sort((a, b) => new Date(b[0].split('.').reverse().join('-')) - new Date(a[0].split('.').reverse().join('-')))
                  .slice(0, 14)
                  .map(([date, data]) => {
                    const maxRevenue = Math.max(...Object.values(salesByDay).map(d => d.revenue));
                    return (
                      <div key={date} className="flex items-center gap-3">
                        <span className="w-20 text-sm text-slate-500">{date}</span>
                        <div className="flex-1 h-6 bg-slate-100 rounded overflow-hidden">
                          <div
                            className="h-full bg-gradient-to-r from-emerald-400 to-emerald-500 rounded"
                            style={{ width: `${(data.revenue / maxRevenue) * 100}%` }}
                          />
                        </div>
                        <div className="text-right w-24">
                          <span className="font-mono font-bold">{data.revenue.toFixed(2)}€</span>
                          <span className="text-xs text-slate-400 ml-1">({data.count})</span>
                        </div>
                      </div>
                    );
                  })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Stock Overview */}
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Package className="w-5 h-5" />
            Bestandsübersicht
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="p-4 bg-emerald-50 rounded-lg text-center">
              <p className="text-3xl font-bold text-emerald-700">{stockSummary.ok}</p>
              <p className="text-sm text-emerald-600">OK</p>
            </div>
            <div className="p-4 bg-amber-50 rounded-lg text-center">
              <p className="text-3xl font-bold text-amber-700">{stockSummary.low}</p>
              <p className="text-sm text-amber-600">Niedrig</p>
            </div>
            <div className="p-4 bg-red-50 rounded-lg text-center">
              <p className="text-3xl font-bold text-red-700">{stockSummary.empty}</p>
              <p className="text-sm text-red-600">Leer</p>
            </div>
            <div className="p-4 bg-orange-50 rounded-lg text-center">
              <p className="text-3xl font-bold text-orange-700">{stockSummary.expiring}</p>
              <p className="text-sm text-orange-600">MHD kritisch</p>
            </div>
          </div>

          {/* Stock List */}
          <div className="space-y-2 max-h-60 overflow-y-auto">
            {stock
              .filter(s => s.status === "low" || s.status === "empty" || s.expiry_status !== "ok")
              .map((item) => (
                <div
                  key={item.product_id}
                  className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                >
                  <div>
                    <p className="font-medium">{item.product_name}</p>
                    <div className="flex gap-2 mt-1">
                      {item.status !== "ok" && (
                        <Badge className={
                          item.status === "empty" ? "bg-red-100 text-red-700" :
                          "bg-amber-100 text-amber-700"
                        }>
                          {item.status === "empty" ? "Leer" : "Niedrig"}
                        </Badge>
                      )}
                      {item.expiry_status !== "ok" && (
                        <Badge className={
                          item.expiry_status === "expired" ? "bg-red-100 text-red-700" :
                          item.expiry_status === "critical" ? "bg-orange-100 text-orange-700" :
                          "bg-amber-100 text-amber-700"
                        }>
                          MHD {item.expiry_status === "expired" ? "abgelaufen" : "kritisch"}
                        </Badge>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-bold">{item.total_quantity} Stk.</p>
                    <p className="text-xs text-slate-500">Min: {item.min_stock}</p>
                  </div>
                </div>
              ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ReportsPage;
